/**
 * The Alpine half of Check my visitors' goal dialog (S55, D87 and D93 of
 * documentation/pr-plans/insights-s0-plan-and-seats.md;
 * livewire/sites/insights/activities/check-my-visitors/goal-dialog): the key events picked, as term chips
 * with a ×, Add from the site's other key events, and the label. Nothing is sent until Save, which hands
 * the picks to CheckMyVisitors::saveGoal(); the server checks every name against what the activity read,
 * so this only offers what it was given.
 *
 * Each opening starts from the saved goal (the dialog shell's `show`, which this sits inside), so a
 * Cancel or an Escape leaves nothing behind. A save re-keys the form, which starts it from the new goal.
 *
 * config: { keyEvents: string[], label: string, choices: string[], strings: { remove, added, removed } },
 * each string holding ":event" where the key event's name goes.
 */
export default function InsightsGoal(config) {
    return {
        ig_hasGoal: config.keyEvents.length > 0,
        ig_chosen: [...config.keyEvents],
        ig_label: config.label,
        ig_choices: config.choices,
        ig_pick: "",
        ig_status: "",
        ig_saving: false,

        init() {
            this.$watch("show", (open) => {
                if (open) {
                    this.ig_reset();
                }
            });
        },

        /** The site's key events not picked yet, in the order the activity read them. */
        get ig_available() {
            return this.ig_choices.filter((choice) => !this.ig_chosen.includes(choice));
        },

        /** The same, as Add's Insights dropdown lists its choices: each key event's name is its value and its label. */
        get ig_addChoices() {
            return this.ig_available.map((choice) => ({ value: choice, label: choice }));
        },

        ig_reset() {
            this.ig_chosen = [...config.keyEvents];
            this.ig_label = config.label;
            this.ig_pick = "";
            this.ig_status = "";
        },

        ig_text(key, event) {
            return config.strings[key].split(":event").join(event);
        },

        ig_add() {
            const event = this.ig_pick;

            if (event === "" || this.ig_chosen.includes(event)) {
                return;
            }

            this.ig_chosen.push(event);
            this.ig_pick = "";
            this.ig_announce(this.ig_text("added", event));

            // The last one added hides Add with the list it picked from: focus goes to its chip's ×
            // rather than to the page.
            if (this.ig_available.length === 0) {
                const root = this.$root;

                this.$nextTick(() => [...root.querySelectorAll("[data-goal-chip-remove]")].pop()?.focus());
            }
        },

        /**
         * Removes a pick, and moves focus to the × that took its place, or the one before it, or to Add once
         * none is left: the pressed × leaves the page with its chip.
         */
        ig_remove(event, button) {
            // Read now, while the pressed button is in the page: once its chip is gone, $root evaluated
            // from it finds no component.
            const root = this.$root;
            const buttons = () => [...root.querySelectorAll("[data-goal-chip-remove]")];
            const index = buttons().indexOf(button);

            this.ig_chosen = this.ig_chosen.filter((chosen) => chosen !== event);
            this.ig_announce(this.ig_text("removed", event));
            this.$nextTick(() => {
                const left = buttons();

                (left[Math.min(index, left.length - 1)] ?? root.querySelector("#insights-goal-add"))?.focus();
            });
        },

        /** Save, once at a time: a second press while one is out is the same save. */
        ig_save() {
            if (this.ig_saving) {
                return;
            }

            this.ig_saving = true;
            this.$wire.saveGoal([...this.ig_chosen], this.ig_label).finally(() => {
                this.ig_saving = false;
            });
        },

        /** Emptied first, so the same words twice in a row still change the region, and are said again. */
        ig_announce(message) {
            this.ig_status = "";
            this.$nextTick(() => {
                this.ig_status = message;
            });
        },
    };
}
